import { Injectable, Injector } from '@angular/core';
import { cloneDeep, find, isEmpty } from '@sama/micro-dash';
import { map } from 'rxjs/operators';
import { TokenManagementService } from '../../../cross-team-shared/misc-services/token-management.service';
import { AbstractCallbackService } from '../../../shared/misc-services/abstract-callback.service';
import { delayExecution } from '../../../shared/misc-utilities/promise-utils';
import {
  SubmitAnswerRequestOptions,
  TaskResource,
} from '../../../shared/resources/task-resource.service';
import { AnswersByName } from '../../../team-apis/otterdam/answers/answer';
import { AutoQaScriptAfterValidation } from '../../../team-apis/otterdam/auto-qa-response';
import { RedirectResponse } from '../../../team-apis/otterdam/redirect-response';
import { TaskExternal } from '../../../team-apis/otterdam/task';
import { CustomJsService } from '../../h3ml/custom-js.service';
import { TaskWorkspaceState } from '../../h3ml/state/task-workspace-state';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { FusionManagerFactory } from '../../outputs/annotation/annotation-shared/fusion/fusion-manager-factory.service';
import { isRedirectResponse } from '../../shared/navigation/is-redirect-response';
import {
  createSubmissionsErrors,
  hasSubmissionErrors,
  SubmissionErrors,
} from '../../shared/utils/submission-errors-utils';
import { TimerService } from '../timer-badge/timer.service';
import { AutoQaService } from './auto-qa.service';
import { SubmissionLoggingService } from './submission-logging.service';

export class TaskSubmissionError extends Error {}
export class TaskSubmissionBenignError extends Error {}

@Injectable()
export class SubmitTaskService extends AbstractCallbackService {
  constructor(
    private autoQaService: AutoQaService,
    private customJsService: CustomJsService,
    private store: TaskWorkspaceStore,
    private taskResource: TaskResource,
    private timerService: TimerService,
    private tokenService: TokenManagementService,
    private fusionManagerFactory: FusionManagerFactory,
    private submissionLoggingService: SubmissionLoggingService,
    injector: Injector,
  ) {
    super(injector);
  }

  async submit(
    qaGroupId: string,
  ): Promise<TaskExternal | RedirectResponse | SubmissionErrors> {
    const submissionErrors = this.runPreSubmissionChecks();
    if (hasSubmissionErrors(submissionErrors)) {
      return submissionErrors;
    }

    const serverErrors = await this.runAsynchronousCallbacks();
    if (serverErrors.length) {
      return createSubmissionsErrors({ serverErrors });
    }

    const { answers, errors: benignErrors } = this.transformAnswers();
    if (benignErrors.length) {
      return createSubmissionsErrors({ benignErrors });
    }

    this.submissionLoggingService.updateStep('autoQAStarted');
    const { blockingErrors, autoQaScriptsAfterValidation } =
      await this.autoQaService.validate(answers);

    if (blockingErrors.length) {
      return createSubmissionsErrors({ blockingErrors });
    }

    this.submissionLoggingService.updateStep('sh3SubmissionStarted');
    return this.submitAndFetchStep(
      qaGroupId,
      answers,
      autoQaScriptsAfterValidation,
    ).finally(() =>
      this.submissionLoggingService.updateStep('submissionCompleted'),
    );
  }

  runPreSubmissionChecks() {
    const benignErrors = this.checkForValidationErrors();
    if (benignErrors.length > 0) {
      return createSubmissionsErrors({
        benignErrors,
      });
    }

    return createSubmissionsErrors();
  }

  unsubmittableReason$() {
    return this.store('outputInfoByName').$.pipe(
      map((outputInfoByName) => {
        const outputInfo = find(
          outputInfoByName,
          (info) => !!info.unsubmittableReason,
        );
        return outputInfo?.unsubmittableReason;
      }),
    );
  }

  transformAnswers() {
    let answers = this.store.extractAnswersFromOutputInfo();
    const customJs = this.store.state().customJs;
    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    if (customJs && customJs.transformAnswers) {
      answers = cloneDeep(answers);
      try {
        customJs.transformAnswers(answers);
      } catch (error) {
        this.customJsService.handleError(error as Error);
        const errors = [(error as Error).message];
        return { errors, answers };
      }
    }
    return { answers, errors: [] };
  }

  private checkForValidationErrors() {
    if (!this.store.isUsingObjects()) {
      return this.checkForValidationErrorsObjectless();
    }
    try {
      let errors = this.checkForCustomJsErrors();
      if (isEmpty(errors)) {
        return [];
      }

      if (!Array.isArray(errors)) {
        errors = [errors];
      }
      this.messagingService.error(
        'You may not submit your answers until you fix these issues:',
        errors,
      );
      return errors;
    } catch (error) {
      this.customJsService.handleError(error as Error);
      return [(error as Error).message];
    }
  }

  private checkForValidationErrorsObjectless() {
    try {
      let errors = this.checkForCustomJsErrors();

      const fusionErrors = this.checkForFusionErrors();
      if (isEmpty(errors) && isEmpty(fusionErrors)) {
        return [];
      }

      if (!Array.isArray(errors)) {
        errors = [errors];
      }
      // fix me if you change this code
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
      errors = errors?.concat(fusionErrors);
      this.messagingService.error(
        'You may not submit your answers until you fix these issues:',
        errors,
      );
      return errors;
    } catch (error) {
      this.customJsService.handleError(error as Error);
      return [(error as Error).message];
    }
  }

  private checkForCustomJsErrors() {
    const customJs = this.store.state().customJs;
    if (customJs?.getValidationErrors) {
      const answers = this.store.extractAnswersFromOutputInfo();
      return customJs.getValidationErrors(answers) || [];
    } else {
      return [];
    }
  }

  private checkForFusionErrors() {
    const answers = this.store.extractAnswersFromOutputInfo();
    const pointCloud = this.store
      .state()
      .project.outputs.find((output) => output.type === 'PointCloudOutput');
    if (!pointCloud) {
      return [];
    }
    return this.fusionManagerFactory
      .getManager(pointCloud.id)
      .validateAnswer(answers, pointCloud.name);
  }

  private async runAsynchronousCallbacks() {
    // Ensure this is asynchronous
    try {
      await delayExecution(() => this.runCallbacks());
    } catch (error) {
      return [(error as Error).message];
    }

    return [];
  }

  private async submitAndFetchStep(
    qaGroupId: string,
    answers: AnswersByName,
    autoQaScriptsAfterValidation?: AutoQaScriptAfterValidation[],
  ) {
    let submitResult: TaskExternal | RedirectResponse;

    try {
      submitResult = await this.submitAnswers(
        answers,
        autoQaScriptsAfterValidation,
        qaGroupId,
      );
    } catch {
      return createSubmissionsErrors({
        benignErrors: ['Submission failed'],
      });
    }

    return this.handleSubmit(submitResult);
  }

  private async submitAnswers(
    answers: AnswersByName,
    autoQaScriptsAfterValidation: AutoQaScriptAfterValidation[] | undefined,
    qaGroupId: string,
  ) {
    // Force refresh the token before submitting, as a workaround for a bug
    // about occasional requests passing expired tokens.
    // https://bodhi.samasource.org/jira/browse/POL-578
    const token = await this.tokenService.getToken('access_token', true);

    const state = this.store.state();
    const submitAnswer: SubmitAnswerRequestOptions = {
      ...this.createBaseSubmitAnswerFromState(state),
      projectId: state.project.id,
      answers,
      autoQaScriptsAfterValidation,
      qaGroupId,
      token,
    };

    return this.taskResource.submit_answer(submitAnswer);
  }

  private async handleSubmit(taskOrRedirect: TaskExternal | RedirectResponse) {
    if (isRedirectResponse(taskOrRedirect)) {
      return taskOrRedirect;
    }

    const task = taskOrRedirect; // now only Task

    if (task._extras && task._extras.message) {
      if (task._extras.error_occurred) {
        this.messagingService.error(task._extras.message);
      } else {
        this.messagingService.show(task._extras.message);
      }
    }

    this.taskResource.setNextReservedTask(task);

    return task;
  }

  private createBaseSubmitAnswerFromState(state: TaskWorkspaceState) {
    return {
      stepId: state.stepId!,
      taskId: state.task.id,
      nextInternalId: state.task.next_internal_id,
      duration: this.timerService.elapsed(),
    };
  }
}
